/**
 * TIME OF DAY on the client (issue #463) — the mirror of
 * `api/src/lib/clockTime.ts`, which holds the argument for the convention.
 * Read that first; only what is client-specific is repeated here.
 *
 * `HH:MM`, 24-hour, local wall-clock. `<input type="time">` produces
 * exactly that string and this renders exactly that string, so nothing on
 * the round trip parses a time into a `Date` and formats it back — which is
 * the whole point. A `new Date("2026-06-12T16:30")` round trip is how a
 * session at 4pm becomes a session at 3am in a browser whose clock disagrees
 * with the server's, and this app has never done it to a DATE either:
 * `training_date` and `match_date` are printed verbatim everywhere they
 * appear. One convention, extended, not a second one introduced.
 */

/** `00:00` through `23:59` — the same expression the API's Zod field uses. */
export const CLOCK_TIME_RE = /^([01]\d|2[0-3]):[0-5]\d$/;

/**
 * The stored value if it is a time, otherwise `""`.
 *
 * NULL, `""` AND GARBAGE ALL COLLAPSE TO `""`, deliberately: every row
 * written before #463 has no time, a coach who knows only the date leaves
 * it empty on purpose, and neither of those is a state worth rendering a
 * placeholder for. Callers test the result for truthiness and simply omit
 * the time — "date only" is a first-class rendering, not a degraded one.
 */
export function formatClockTime(time) {
  const t = String(time ?? "").trim();
  return CLOCK_TIME_RE.test(t) ? t : "";
}

/**
 * "when", for the one-line summaries that already print a bare date: the
 * date alone when there is no time, and `date · time` when there is.
 *
 * Used by the training-session card and the match list/table so that a time
 * saved in the form is visible in every place the session or match is shown
 * — a time stored and never rendered is the bug this helper exists to make
 * hard to reintroduce.
 */
export function dateTimeLabel(date, time) {
  const d = String(date ?? "").trim();
  const t = formatClockTime(time);
  if (!d) return t;
  return t ? `${d} · ${t}` : d;
}

/**
 * B6(4) (PR #880 review round 2, issue #762) — `SeasonRecord.dc.html`'s own
 * drawn match card shows the time 12-hour with a lowercase am/pm ("2:00
 * pm"), not this file's `HH:MM` 24-hour convention every OTHER caller uses
 * (`formatClockTime`/`dateTimeLabel` above — the match history table/card,
 * training sessions). A SEPARATE, additive function, not a rewrite of
 * either: `formatClockTime`'s own 24-hour output is unchanged for every
 * existing caller, and this one screen's own card composes its line with
 * this instead (`SeasonRecordScreen.jsx`'s `MatchRow`).
 *
 * Built ON `formatClockTime`, not a second parse of the raw value — the
 * same "stored `HH:MM` is the only shape trusted" rule, one conversion
 * step further, never a `Date` round trip (this file's own header on why
 * that specific round trip is the bug, not the fix).
 */
export function formatClockTime12h(time) {
  const t = formatClockTime(time);
  if (!t) return "";
  const [hStr, mStr] = t.split(":");
  const h = Number(hStr);
  const period = h < 12 ? "am" : "pm";
  const h12 = h % 12 === 0 ? 12 : h % 12;
  return `${h12}:${mStr} ${period}`;
}
